/* eslint-disable max-lines */
import cn from 'classnames';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { SingleValue } from 'react-select';
import Input from '@components/atoms/ui-kit/input.tsx';
import React, { useState } from 'react';
import { TeamPermission } from '@components/workspace/create-folder.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import toast from 'react-hot-toast';
import { UpdateFolderNameMutationParams } from '@utils/api/folders/use-update-folder.tsx';
import { WorkspaceRow } from '@components/main-workspace/workspace.tsx';
import { useGetGroups } from '@utils/api/groups/use-get-groups.tsx';
import { useCreateFolder } from '@utils/api/folders/use-create-folder.tsx';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { Eye, Pencil, Lock, X, Plus } from 'lucide-react';

type Props = {
  selectedItem: WorkspaceRow;
  teamPermissions: TeamPermission[];
  setTeamPermissions: React.Dispatch<React.SetStateAction<TeamPermission[]>>;
  copyFolderModalOpen: boolean;
  setCopyFolderModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedItem: React.Dispatch<
    React.SetStateAction<WorkspaceRow | undefined>
  >;
};

export const CopyFolderForm = ({
  selectedItem,
  setSelectedItem,
  teamPermissions,
  setTeamPermissions,
  copyFolderModalOpen,
  setCopyFolderModalOpen
}: Props) => {
  const { t } = useTranslation();
  const { getColour, getBrandTint, getContrastTextColor } = useThemeContext();
  const { data: groups } = useGetGroups({
    enabled: copyFolderModalOpen,
    onError: (error: AxiosError) => {
      if (error?.response?.status === 403) return;
    }
  });
  const [isCopying, setIsCopying] = useState(false);
  const [newName, setNewName] = useState('');
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [addHovered, setAddHovered] = useState(false);
  const createFolderMutation = useCreateFolder();

  const activePillStyle = {
    backgroundColor: getColour('activeText'),
    borderColor: getColour('activeText'),
    color: getContrastTextColor(getColour('activeText'))
  };
  const inactivePillStyle = {
    backgroundColor: getColour('secondLayer'),
    borderColor: getColour('hairline')
  };

  const permissionsList = [
    { id: 0, label: t('private'), icon: <Lock className="h-3.5 w-3.5" /> },
    { id: 1, label: t('read'), icon: <Eye className="h-3.5 w-3.5" /> },
    { id: 2, label: t('write'), icon: <Pencil className="h-3.5 w-3.5" /> }
  ];

  const handleAddTeam = () => {
    setTeamPermissions(prev => [...prev, { groupId: '', permission: 2 }]);
  };

  const handleRemoveTeam = (index: number) => {
    setTeamPermissions(prev => prev.filter((_, i) => i !== index));
  };

  const handlePermissionChange = (index: number, permissionId: number) => {
    const updated = [...teamPermissions];
    updated[index].permission = permissionId;
    setTeamPermissions(updated);
  };

  const handleGroupChange = (
    index: number,
    selectedOption: SingleValue<{ label: string; value: string }>
  ) => {
    if (selectedOption) {
      const updated = [...teamPermissions];
      updated[index].groupId = selectedOption.value;
      setTeamPermissions(updated);
    }
  };

  const saveNewName = async () => {
    if (!selectedItem) {
      toast.error(t('no-folder-selected'));
      return;
    }
    setIsCopying(true);
    const payload: UpdateFolderNameMutationParams = {
      name: newName || selectedItem.name || 'Copy of doc.',
      folder_id: Number(selectedItem.id),
      permissions: teamPermissions
        .filter(tp => tp.groupId)
        .map(tp => ({
          group_id: parseInt(tp.groupId, 10),
          permission: tp.permission
        })),
      parent_id: selectedItem.parent_id || null
    };

    try {
      await createFolderMutation.mutateAsync(payload);
      toast.success(t('folder-created-successfully'));
      setIsCopying(false);
      setCopyFolderModalOpen(false);
      setSelectedItem(undefined);
    } catch (error) {
      const err = error as AxiosError<BaseError>;
      const fallbackMessage = t('an-error-occurred-while-creating-the-folder');
      const data = err?.response?.data;
      let messages: string[] = [];
      if (Array.isArray(data?.detail)) {
        type DetailItem = { loc?: unknown[]; msg: string };
        messages = (data.detail as DetailItem[]).map(e => {
          const field = (e.loc?.[1] as string) ?? 'field';
          return `${field}: ${e.msg}`;
        });
      } else if (data?.message) {
        messages = [data.message];
      } else if (data?.error_description) {
        messages = [data.error_description];
      } else {
        messages = [fallbackMessage];
      }
      setIsCopying(false);
      toast.error(messages.join('\n'));
    }
  };

  const FORM_ID = 'copy-folder-form';

  return (
    <Modal
      isOpen={copyFolderModalOpen}
      onClose={() => {
        setCopyFolderModalOpen(false);
        setSelectedItem(undefined);
      }}
      size="medium"
      title={`${t('copy-folder')} — ${selectedItem.name}`}
      footerActions={[
        <Button
          key="cancel"
          type="button"
          onClick={() => {
            setCopyFolderModalOpen(false);
            setSelectedItem(undefined);
          }}
          label={t('cancel')}
          size="small"
          variant="smoke"
        />,
        <Button
          key="save"
          type="submit"
          form={FORM_ID}
          label={isCopying ? t('copying') : t('copy')}
          size="small"
          variant="primary"
          loading={isCopying}
        />
      ]}
    >
      <form
        id={FORM_ID}
        className="grid gap-6 py-2"
        onSubmit={e => {
          e.preventDefault();
          saveNewName();
        }}
      >
        <button type="submit" className="hidden" aria-hidden="true" />

        {/* Folder name */}
        <div>
          <label
            htmlFor="filename"
            className="mb-1.5 block text-xs font-semibold uppercase tracking-wide"
            style={{ color: getColour('inactiveText') }}
          >
            {t('name')}
          </label>
          <Input
            id="filename"
            defaultValue={selectedItem.name}
            onChange={e => setNewName(e.target.value)}
            type="text"
            placeholder={selectedItem.name}
            autoComplete="off"
            className="block w-full"
          />
        </div>

        {/* Permissions */}
        <div>
          <p
            className="mb-3 text-xs font-semibold uppercase tracking-wide"
            style={{ color: getColour('inactiveText') }}
          >
            {t('folder-group-and-permisions')}
          </p>

          <div className="space-y-3">
            {teamPermissions.map((teamPermission, index) => (
              <div
                key={index}
                className="relative rounded-xl border border-hairline bg-app-soft p-4"
              >
                {/* Remove button */}
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveTeam(index)}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                    className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full transition-colors"
                    style={{
                      color:
                        hoveredRow === index
                          ? 'rgb(239, 68, 68)'
                          : getColour('inactiveText'),
                      backgroundColor:
                        hoveredRow === index
                          ? 'rgba(239, 68, 68, 0.1)'
                          : 'transparent'
                    }}
                  >
                    <X size={13} />
                  </button>
                )}

                {/* Group select */}
                <div className={cn(index > 0 && 'pr-8')}>
                  <label
                    htmlFor={`role-${index}`}
                    className="mb-1.5 block text-xs font-medium"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('group')}
                  </label>
                  <SelectInput
                    id={`role-${index}`}
                    className="w-full text-sm"
                    options={
                      groups?.result?.map(group => ({
                        label: group.name,
                        value: group.id.toString()
                      })) || []
                    }
                    onChange={option => handleGroupChange(index, option)}
                    value={
                      groups?.result?.find(
                        g => g.id.toString() === teamPermission.groupId
                      )
                        ? {
                            label:
                              groups?.result?.find(
                                g => g.id.toString() === teamPermission.groupId
                              )?.name || '',
                            value: teamPermission.groupId
                          }
                        : null
                    }
                    isMulti={false}
                  />
                </div>

                {/* Permission pills */}
                <div className="mt-3">
                  <div
                    className="mb-1.5 text-xs font-medium"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('permissions')}
                  </div>
                  <div className="flex gap-2">
                    {permissionsList.map(option => (
                      <label
                        key={option.id}
                        aria-label={option.label}
                        className={cn(
                          'group relative flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-full border py-2 text-xs font-semibold transition-colors',
                          'has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-primary',
                          'has-disabled:opacity-40 has-disabled:cursor-not-allowed'
                        )}
                        style={
                          teamPermission.permission === option.id
                            ? activePillStyle
                            : inactivePillStyle
                        }
                      >
                        <input
                          type="radio"
                          name={`permission-${index}`}
                          value={option.id}
                          checked={teamPermission.permission === option.id}
                          onChange={e =>
                            handlePermissionChange(
                              index,
                              Number(e.target.value)
                            )
                          }
                          className="absolute inset-0 appearance-none cursor-pointer focus:outline-none disabled:cursor-not-allowed"
                        />
                        <span className="shrink-0">{option.icon}</span>
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add team — dashed full-width trigger */}
          <button
            type="button"
            onClick={handleAddTeam}
            onMouseEnter={() => setAddHovered(true)}
            onMouseLeave={() => setAddHovered(false)}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3 text-sm font-medium transition-all duration-200"
            style={{
              borderColor: addHovered
                ? getColour('activeText')
                : getColour('inactiveText'),
              color: addHovered
                ? getColour('activeText')
                : getColour('inactiveText'),
              backgroundColor: addHovered ? getBrandTint(0.04) : 'transparent'
            }}
          >
            <Plus size={15} />
            {t('add-team')}
          </button>
        </div>
      </form>
    </Modal>
  );
};
